import { Link } from 'react-router-dom'
import { DEFAULT_PRECISION, PRICE_TYPES } from 'utils/constants'
import { unformatStringNumber } from 'utils/helpers'

export const transformMaterialTypes = (materialTypesData, defaultCurrency, defaultYardUm) =>
  (materialTypesData || [])
    .filter(materialType => !!materialType?.defaultPriceEntry)
    .map(materialType => ({
      id: materialType?.defaultPriceEntry?.id,
      name: materialType?.commonName,
      code: materialType?.code,
      weightUnits: materialType?.defaultPriceEntry?.weightUnits || defaultYardUm,
      buyPrice: {
        ...(materialType?.defaultPriceEntry?.buyPrice || {}),
        isBuyPrice: true,
        priceType: materialType?.defaultPriceEntry?.buyPrice?.priceType || PRICE_TYPES.SPOT.id,
        price: materialType?.defaultPriceEntry?.buyPrice?.price || {
          amount: 0,
          currency: defaultCurrency,
          precision: 4,
        },
        minMaxRange: materialType?.defaultPriceEntry?.buyPrice?.minMaxRange || 0,
      },
      sellPrice: {
        ...(materialType?.defaultPriceEntry?.sellPrice || {}),
        isBuyPrice: false,
        priceType: materialType?.defaultPriceEntry?.sellPrice?.priceType || PRICE_TYPES.SPOT.id,
        price: materialType?.defaultPriceEntry?.sellPrice?.price || {
          amount: 0,
          currency: defaultCurrency,
          precision: 4,
        },
        minMaxRange: materialType?.defaultPriceEntry?.sellPrice?.minMaxRange || 0,
      },
      materialTypeId: materialType?.id,
      priceSheetId: materialType?.defaultPriceEntry?.priceSheetId,
      commodityId: materialType?.commodityId,
    }))

export const getFormulaValues = (priceEntry, getValues, defaultCurrency, defaultYardUm) => {
  const buyBenchmark = getValues('pricing.buyBenchmark' || null)
  const buyFormula = getValues('pricing.buyFormula') || 'SPOT'
  const buyDifferential = getValues('pricing.buyDifferential') || 0
  const buyOtherCosts = getValues('pricing.buyOtherCosts') || 0
  const buyPercentageValue = getValues('pricing.buyRecovery') || 100
  const buyPriceValue = getValues('pricing.buyPrice') || 0
  const buyReferenceId = buyBenchmark?.id
  const buyMinMaxRange = getValues('pricing.buyMinMaxRange') || 0

  const sellBenchmark = getValues('pricing.sellBenchmark' || null)
  const sellFormula = getValues('pricing.sellFormula') || 'SPOT'
  const sellDifferential = getValues('pricing.sellDifferential') || 0
  const sellOtherCosts = getValues('pricing.sellOtherCosts') || 0
  const sellPercentageValue = getValues('pricing.sellRecovery') || 100
  const sellPriceValue = getValues('pricing.sellPrice') || 0
  const sellReferenceId = sellBenchmark?.id
  const sellMinMaxRange = getValues('pricing.sellMinMaxRange') || 0

  const unit = getValues('pricing.buyUnit') || defaultYardUm
  const formulaValues = {
    id: priceEntry.id,
    materialTypeId: priceEntry.materialTypeId,
    priceSheetId: priceEntry.priceSheetId,
    buyPrice: {
      ...priceEntry?.buyPrice,
      isBuyPrice: true,
      price: {
        amount: Math.floor(
          (unformatStringNumber(buyPriceValue) || 0) *
            10 ** (priceEntry?.buyPrice?.price?.precision || DEFAULT_PRECISION),
        ),
        currency: defaultCurrency,
        precision: priceEntry?.buyPrice?.price?.precision || DEFAULT_PRECISION,
      },
      priceType: buyFormula,
      minMaxRange: buyMinMaxRange,
      differential: {
        amount: Math.floor(
          (unformatStringNumber(buyDifferential) || 0) *
            10 ** (priceEntry?.buyPrice?.differential?.precision || DEFAULT_PRECISION),
        ),
        currency: defaultCurrency,
        precision: priceEntry?.buyPrice?.differential?.precision || DEFAULT_PRECISION,
      },
      recoveryPercentage: Math.floor(unformatStringNumber(buyPercentageValue) || 100),
      otherCosts: {
        amount: Math.floor(
          (unformatStringNumber(buyOtherCosts) || 0) *
            10 ** (priceEntry?.buyPrice?.otherCosts?.precision || DEFAULT_PRECISION),
        ),
        currency: defaultCurrency,
        precision: priceEntry?.buyPrice?.otherCosts?.precision || DEFAULT_PRECISION,
      },
      benchmark: buyBenchmark,
    },
    sellPrice: {
      ...priceEntry?.sellPrice,
      isBuyPrice: false,
      price: {
        amount: Math.floor(
          (unformatStringNumber(sellPriceValue) || 0) *
            10 ** (priceEntry?.sellPrice?.price?.precision || DEFAULT_PRECISION),
        ),
        currency: defaultCurrency,
        precision: priceEntry?.sellPrice?.price?.precision || DEFAULT_PRECISION,
      },
      priceType: sellFormula,
      minMaxRange: sellMinMaxRange,
      differential: {
        amount: Math.floor(
          (unformatStringNumber(sellDifferential) || 0) *
            10 ** (priceEntry?.sellPrice?.differential?.precision || DEFAULT_PRECISION),
        ),
        currency: defaultCurrency,
        precision: priceEntry?.sellPrice?.differential?.precision || DEFAULT_PRECISION,
      },
      recoveryPercentage: Math.floor(unformatStringNumber(sellPercentageValue) || 100),
      otherCosts: {
        amount: Math.floor(
          (unformatStringNumber(sellOtherCosts) || 0) *
            10 ** (priceEntry?.sellPrice?.otherCosts?.precision || DEFAULT_PRECISION),
        ),
        currency: defaultCurrency,
        precision: priceEntry?.sellPrice?.otherCosts?.precision || DEFAULT_PRECISION,
      },
      benchmark: sellBenchmark,
    },
    weightUnits: unit,
  }

  if (buyBenchmark?.history) {
    formulaValues.buyPrice.benchmarkId = buyReferenceId
    delete formulaValues.buyPrice.referencePriceEntryId
  } else {
    formulaValues.buyPrice.referencePriceEntryId = buyReferenceId
    delete formulaValues.buyPrice.benchmarkId
  }

  if (sellBenchmark?.history) {
    formulaValues.sellPrice.benchmarkId = sellReferenceId
    delete formulaValues.sellPrice.referencePriceEntryId
  } else {
    formulaValues.sellPrice.referencePriceEntryId = sellReferenceId
    delete formulaValues.sellPrice.benchmarkId
  }
  return formulaValues
}

export const commodityColumns = [
  {
    field: 'commonName',
    headerName: 'Material Name',
    flex: 1,
    renderCell: params => {
      const {
        row: { commodityId, commonName, id },
      } = params
      const materialName = encodeURIComponent(commonName)
      return <Link to={`/app/commodities/${commodityId}/${materialName}/${id}/overview`}>{commonName}</Link>
    },
  },
  {
    field: 'code',
    headerName: 'Code',
    flex: 1,
  },
  {
    field: 'buyPrice',
    headerName: 'Default Buy Price',
    flex: 1,
    headerAlign: 'right',
    align: 'right',
    sortable: false,
  },
  {
    field: 'sellPrice',
    headerName: 'Default Sell Price',
    flex: 1,
    headerAlign: 'right',
    align: 'right',
    sortable: false,
  },
]
